/**
 * Pure data-shaping helpers for analytics SVG charts.
 * No DOM dependencies — unit-testable without a browser.
 */

/** Map a value in [domainMin, domainMax] to a pixel coordinate in [rangeMin, rangeMax]. */
export function linearScale(
  value: number,
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number
): number {
  if (domainMax === domainMin) return (rangeMin + rangeMax) / 2;
  return rangeMin + ((value - domainMin) / (domainMax - domainMin)) * (rangeMax - rangeMin);
}

/**
 * Normalise a series of counts to proportional widths in [0, maxWidth].
 * Returns an array of pixel widths; the largest item gets maxWidth.
 * Empty input returns [].
 */
export function normaliseToWidth(counts: number[], maxWidth: number): number[] {
  if (counts.length === 0) return [];
  const max = Math.max(...counts);
  if (max === 0) return counts.map(() => 0);
  return counts.map((c) => (c / max) * maxWidth);
}

/**
 * Compute evenly-spaced x-coordinates for n bars in a plot area starting at
 * plotX with total plotW width.  Returns centre x for each bar plus the
 * computed bar width (leaving a 20% gap between bars).
 */
export function computeBarLayout(
  n: number,
  plotX: number,
  plotW: number
): { barWidth: number; centres: number[] } {
  if (n === 0) return { barWidth: 0, centres: [] };
  const slotW = plotW / n;
  const barWidth = Math.max(1, slotW * 0.8);
  const centres = Array.from({ length: n }, (_, i) => plotX + slotW * i + slotW / 2);
  return { barWidth, centres };
}

/** Colour token for a status category. Falls back to outline-variant. */
export function categoryColor(category: string): string {
  switch (category) {
    case "done":
      return "var(--color-status-done)";
    case "started":
    case "review":
      return "var(--color-status-in-progress)";
    case "cancelled":
      return "var(--color-status-blocked)";
    case "backlog":
    case "unstarted":
    default:
      return "var(--color-status-backlog)";
  }
}

/**
 * Format a decimal hours value as "Xh Ym".
 * 0 → "0h", 1.5 → "1h 30m", 0.25 → "15m".
 */
export function formatHours(hours: number): string {
  if (hours === 0) return "0h";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Discrete 0–4 intensity bucket for a heatmap cell, relative to the largest
 * value in the visible grid (#64 workload heatmap). A bucket rather than a
 * continuous ratio, so "how busy" stays legible as a handful of steps instead
 * of many near-identical shades of the same accent.
 */
export function heatLevel(value: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (value <= 0 || max <= 0) return 0;
  const ratio = value / max;
  if (ratio > 0.75) return 4;
  if (ratio > 0.5) return 3;
  if (ratio > 0.25) return 2;
  return 1;
}
